import { useEffect, useMemo, useRef, useState } from "react";
import api from "../utils/api/axiosInstance";
import { resolveSearchParamsForProducts } from "../utils/resolveComparisonAiSession";
import {
  buildShortlistAiCacheKey,
  fetchShortlistAiCached,
  readShortlistAiCache,
} from "../utils/shortlistAiCache";
import {
  buildFallbackRecommendation,
  buildQuickComparisonTable,
  dedupeCategories,
  getDefaultCategories,
  getDeterministicWinnerIndex,
  getTopMatchIndex,
  getValuePickIndex,
  getWinnerIndex,
  MAX_COMPARISON_ROWS,
} from "../utils/quickComparisonUtils";

const enrichProduct = (base, fetched) => {
  if (!fetched) return base;
  return {
    ...fetched,
    matchScore: fetched.matchScore ?? base?.matchScore,
    rank: fetched.rank ?? base?.rank,
    matchExplanation: base?.matchExplanation ?? fetched.matchExplanation,
  };
};

const buildProductKey = (list = []) =>
  list.map((p) => p?._id || p?.id).filter(Boolean).join(",");

export default function useQuickComparison({ products = [], searchParams = null }) {
  const [enrichedProducts, setEnrichedProducts] = useState(products);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // True only after quick-comparison has finished (success or fail) for the
  // current shortlist — used so shortlist-coach doesn't race / double-fire.
  const [aiSettled, setAiSettled] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  // Only flip after enrichment for this productKey finishes — prevents a
  // premature AI call (and a second one when lockedWinner settles).
  const [enrichmentReadyKey, setEnrichmentReadyKey] = useState("");

  const productKey = buildProductKey(products);
  const productsRef = useRef(products);
  productsRef.current = products;

  const resolvedSearchParams = useMemo(
    () => resolveSearchParamsForProducts(enrichedProducts, searchParams),
    [enrichedProducts, searchParams],
  );

  const searchParamsKey = useMemo(
    () => JSON.stringify(resolvedSearchParams || null),
    [resolvedSearchParams],
  );

  useEffect(() => {
    setEnrichedProducts(productsRef.current);
    setEnrichmentReadyKey("");
    setAiSettled(false);
    setAiPlan(null);
  }, [productKey]);

  useEffect(() => {
    const currentProducts = productsRef.current;
    if (!currentProducts.length) {
      setProductsLoading(false);
      setEnrichmentReadyKey("");
      return undefined;
    }

    let cancelled = false;
    setProductsLoading(true);

    Promise.all(
      currentProducts.map((p) =>
        api.get(`/products/${p._id}`).then((res) => enrichProduct(p, res.data)),
      ),
    )
      .then((resolved) => {
        if (cancelled) return;
        setEnrichedProducts(resolved);
        setEnrichmentReadyKey(productKey);
      })
      .catch(() => {
        if (cancelled) return;
        setEnrichedProducts(currentProducts);
        setEnrichmentReadyKey(productKey);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productKey]);

  // Ground truth winner for the *default* rows, computed before the AI even
  // responds. We lock the AI's pick to this product so its narrative can
  // never point at a different product than the one the UI highlights.
  const baselineTable = useMemo(
    () =>
      buildQuickComparisonTable({
        products: enrichedProducts,
        categories: getDefaultCategories(resolvedSearchParams),
        searchParams: resolvedSearchParams,
      }),
    [enrichedProducts, resolvedSearchParams],
  );

  const baselineWinnerIndex = useMemo(
    () => getDeterministicWinnerIndex({ table: baselineTable, products: enrichedProducts }),
    [baselineTable, enrichedProducts],
  );

  const lockedWinnerProductId =
    enrichedProducts[baselineWinnerIndex >= 0 ? baselineWinnerIndex : 0]?._id || null;

  const latestRef = useRef();
  latestRef.current = {
    enrichedProducts,
    resolvedSearchParams,
    lockedWinnerProductId,
  };

  const completedFetchKeyRef = useRef(null);

  useEffect(() => {
    if (products.length < 2) {
      setAiPlan(null);
      setAiSettled(false);
      completedFetchKeyRef.current = null;
      return undefined;
    }

    // Wait until enrichment for *this* shortlist finished.
    if (productsLoading || enrichmentReadyKey !== productKey) {
      return undefined;
    }

    const requestKey = buildShortlistAiCacheKey(productKey, searchParamsKey);
    if (completedFetchKeyRef.current === requestKey) {
      return undefined;
    }

    const cached = readShortlistAiCache("quickComparison", requestKey);
    if (cached !== undefined) {
      completedFetchKeyRef.current = requestKey;
      setAiPlan(cached);
      setAiLoading(false);
      setAiSettled(true);
      return undefined;
    }

    const {
      enrichedProducts: currentProducts,
      resolvedSearchParams: currentSearchParams,
      lockedWinnerProductId: currentLockedWinnerId,
    } = latestRef.current;

    let cancelled = false;
    setAiLoading(true);
    setAiSettled(false);

    fetchShortlistAiCached({
      namespace: "quickComparison",
      key: requestKey,
      fetcher: async () => {
        const res = await api.post("/ai/grok/quick-comparison", {
          products: currentProducts.map((p) => ({
            _id: p._id,
            name: p.name,
            brand: p.brand,
            price: p.price,
            category: p.category,
            matchScore: p.matchScore,
            specifications: p.specifications || [],
          })),
          searchParams: currentSearchParams,
          lockedWinnerProductId: currentLockedWinnerId,
        });
        return res.data?.comparison || null;
      },
    })
      .then(({ data }) => {
        if (cancelled) return;
        completedFetchKeyRef.current = requestKey;
        setAiPlan(data);
      })
      .catch(() => {
        if (cancelled) return;
        completedFetchKeyRef.current = requestKey;
        setAiPlan(null);
      })
      .finally(() => {
        if (!cancelled) {
          setAiLoading(false);
          setAiSettled(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    productKey,
    searchParamsKey,
    productsLoading,
    enrichmentReadyKey,
    products.length,
  ]);

  const categories = useMemo(() => {
    const source =
      aiPlan?.categories?.length > 0
        ? aiPlan.categories
        : getDefaultCategories(resolvedSearchParams);
    return dedupeCategories(source).slice(0, MAX_COMPARISON_ROWS);
  }, [aiPlan, resolvedSearchParams]);

  const table = useMemo(
    () =>
      buildQuickComparisonTable({
        products: enrichedProducts,
        categories,
        searchParams: resolvedSearchParams,
      }),
    [enrichedProducts, categories, resolvedSearchParams],
  );

  // The badge/checkmarks the user actually sees always win the tie-break —
  // the AI narrative is only trusted when it's talking about that same
  // product. This is what keeps "PICK" and the row checkmarks from ever
  // contradicting the sentence above them.
  const winnerIndex = useMemo(
    () => getDeterministicWinnerIndex({ table, products: enrichedProducts }),
    [table, enrichedProducts],
  );

  const valuePickIndex = useMemo(
    () => getValuePickIndex({ table, products: enrichedProducts }),
    [table, enrichedProducts],
  );

  const topMatchIndex = useMemo(
    () => getTopMatchIndex(enrichedProducts),
    [enrichedProducts],
  );

  const isValuePick = useMemo(
    () =>
      topMatchIndex >= 0 &&
      valuePickIndex >= 0 &&
      valuePickIndex !== topMatchIndex,
    [topMatchIndex, valuePickIndex],
  );

  const recommendation = useMemo(() => {
    const narrativeIndex = isValuePick
      ? valuePickIndex
      : topMatchIndex >= 0
        ? topMatchIndex
        : winnerIndex >= 0
          ? winnerIndex
          : 0;
    const resolvedIndex = narrativeIndex >= 0 ? narrativeIndex : 0;
    const winnerId = enrichedProducts[resolvedIndex]?._id;
    const aiWinnerIndex = getWinnerIndex(
      enrichedProducts,
      aiPlan?.recommendation?.winnerProductId,
    );

    const aiAgreesWithVisibleWinner =
      aiPlan?.recommendation?.summary &&
      String(aiPlan.recommendation.winnerProductId) === String(winnerId) &&
      aiWinnerIndex === resolvedIndex;

    if (aiAgreesWithVisibleWinner && !isValuePick) {
      return aiPlan.recommendation;
    }

    return buildFallbackRecommendation(
      enrichedProducts,
      resolvedSearchParams,
      resolvedIndex,
    );
  }, [
    aiPlan,
    enrichedProducts,
    resolvedSearchParams,
    winnerIndex,
    valuePickIndex,
    topMatchIndex,
    isValuePick,
  ]);

  return {
    table,
    recommendation,
    winnerIndex,
    valuePickIndex,
    topMatchIndex,
    isValuePick,
    loading: productsLoading || aiLoading,
    aiLoading,
    aiSettled,
  };
}
